import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ExternalLink,
  Plus,
  Star,
  Trash2,
  Video,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  resolveInterviewDisplayStatus,
  STAKEHOLDER_ANSWERS_IMPORTED,
} from "../../../components/interviewList/status";
import { TermNotionLink } from "../../../components/term/TermNotionLink";
import {
  useEnsureInterviewScript,
  useInterviewScriptGraphQL,
  useMyInterviewGraphQL,
  useMyInterviewMutations,
} from "../../../hooks/useMyInterviews";
import { MemoTextarea } from "./MemoTextarea";

const STATUS_LABELS: Record<string, string> = {
  SCHEDULED: "インタビュー実施前",
  IN_PROGRESS: "実施中",
  COMPLETED: "完了",
  INTERVIEWED: "インタビュー実施済み",
  SUMMARY_CHECK_PENDING: "要約チェック待ち",
  SUMMARY_CHECK_COMPLETED: "要約チェック完了",
  CANCELLED: "キャンセル済み",
  [STAKEHOLDER_ANSWERS_IMPORTED]: "回答取り込み済み",
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  SCHEDULED: { bg: "#e0f2fe", color: "#0369a1" },
  IN_PROGRESS: { bg: "#fef9c3", color: "#a16207" },
  COMPLETED: { bg: "#dcfce7", color: "#15803d" },
  INTERVIEWED: { bg: "#dcfce7", color: "#15803d" },
  SUMMARY_CHECK_PENDING: { bg: "#fef9c3", color: "#a16207" },
  SUMMARY_CHECK_COMPLETED: { bg: "#f3f4f6", color: "#374151" },
  CANCELLED: { bg: "#fee2e2", color: "#991b1b" },
  [STAKEHOLDER_ANSWERS_IMPORTED]: { bg: "#dcfce7", color: "#15803d" },
};

// 完了報告フォームを操作不能にする終端ステータス。CANCELLED 以外は backend が upsert で
// 受けるので、INTERVIEWED / SUMMARY_CHECK_* も再編集を許可する。
const COMPLETION_REPORT_LOCKED_STATUSES = ["CANCELLED"];

export function InterviewerScripts() {
  const { interviewId } = useParams<{ interviewId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // この画面は複数の遷移元（ターム詳細・インタビュー一覧・ダッシュボード）から開かれるため、
  // 固定リンクではなく遷移元へ戻す。アプリ内遷移で来た場合のみ直前へ戻り、
  // URL直アクセス（初期エントリは location.key === "default"）はダッシュボードへ。
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/employee_interview/interviewer/dashboard");
    }
  };

  const {
    interview,
    loading: interviewLoading,
    error: interviewError,
    refetch: refetchInterview,
  } = useMyInterviewGraphQL(interviewId ?? null);
  const {
    scriptItems,
    loading: scriptLoading,
    error: scriptError,
    refetch: refetchScript,
  } = useInterviewScriptGraphQL(interviewId ?? null);
  const ensureInterviewScript = useEnsureInterviewScript();
  const { updateScriptItem, bulkSaveScriptItems, completeInterview } =
    useMyInterviewMutations();

  // 初回マウント時に未作成スクリプトをテンプレートから生成する。
  // 参照系 Query で書き込みを行わない設計のため Mutation で分離している。
  const ensureCalledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!interviewId || ensureCalledRef.current === interviewId) return;
    ensureCalledRef.current = interviewId;
    ensureInterviewScript(interviewId)
      .then(() => refetchScript())
      .catch(() => {
        // 失敗時は通常の Query 結果（空配列）で UI を表示し続ける
      });
  }, [interviewId, ensureInterviewScript, refetchScript]);

  const [saving, setSaving] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isEditingScript, setIsEditingScript] = useState(false);
  const [editingScriptItems, setEditingScriptItems] = useState<
    Array<{
      scriptItemId?: string;
      category: string;
      timeEstimate: string;
      itemName: string;
      question: string;
      note: string;
      isRequired: boolean;
      order: number;
    }>
  >([]);

  const [handoverNote, setHandoverNote] = useState("");
  const [coachReportNg, setCoachReportNg] = useState<boolean | null>(null);
  const [interviewerInsight, setInterviewerInsight] = useState("");
  const [internalUseAllowed, setInternalUseAllowed] = useState<boolean | null>(
    null,
  );
  const [hasConfidentialInfo, setHasConfidentialInfo] = useState(false);
  const [confidentialInfo, setConfidentialInfo] = useState("");
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const [editingCell, setEditingCell] = useState<{
    itemId: string;
    field: "question" | "note" | "interviewerMemo";
  } | null>(null);
  const [editingValue, setEditingValue] = useState("");

  useEffect(() => {
    if (interview?.completionReport) {
      const report = interview.completionReport;
      setHandoverNote(report.handoverNote || "");
      setCoachReportNg(report.coachReportNg);
      setInterviewerInsight(report.interviewerInsight || "");
      setInternalUseAllowed(report.internalUseAllowed);
      setHasConfidentialInfo(report.hasConfidentialInfo);
      setConfidentialInfo(report.confidentialInfo || "");
    }
  }, [interview]);

  // 保存のたびに refetch が走り urql の fetching が true に戻るため、
  // loading をそのまま全画面の差し替えに使うと書き込むたびに画面が消えてスクロールが先頭に戻る。
  // 全画面を出してよいのは、まだ表示できるデータが何も無い初回の読み込みだけ。
  const initialLoading = interviewLoading && !interview;
  const scriptInitialLoading = scriptLoading && scriptItems.length === 0;
  // 止めるのは台本の一括編集の開始だけ。質問・補足のインライン編集とメモ欄は
  // 変更した1項目しか送らないので、古い内容で書き戻る経路にならない。
  // 再取得中は urql が直前の結果を返し続けるので、表には保存前の内容が残る。
  // その状態で一括編集を始めると古い内容をひな型にしてしまい、保存で書き戻ってしまう
  // （一括保存は既存項目を全削除してから作り直すため）。
  const scriptBulkEditDisabled = scriptLoading;
  const fetchError = interviewError || scriptError;

  const sortedItems = scriptItems
    ? [...scriptItems].sort((a, b) => a.order - b.order)
    : [];

  const handleUpdateScriptItem = useCallback(
    async (
      itemId: string,
      fields: {
        answerMemo?: string;
        interviewerMemo?: string;
        question?: string;
        note?: string;
      },
    ) => {
      setSaving(itemId);
      setError(null);
      try {
        const updatedItem = await updateScriptItem({
          scriptItemId: itemId,
          ...fields,
        });
        if (updatedItem) {
          refetchScript();
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "更新中にエラーが発生しました",
        );
      } finally {
        setSaving(null);
      }
    },
    [updateScriptItem, refetchScript],
  );

  const handleStartEditScript = () => {
    if (scriptBulkEditDisabled) return;

    setEditingScriptItems(
      sortedItems.map((item) => ({
        scriptItemId: item.scriptItemId,
        category: item.category,
        timeEstimate: item.timeEstimate || "",
        itemName: item.itemName,
        question: item.question || "",
        note: item.note || "",
        isRequired: item.isRequired,
        order: item.order,
      })),
    );
    setIsEditingScript(true);
  };

  const handleCancelEditScript = () => {
    if (window.confirm("編集をキャンセルしますか？変更は保存されません。")) {
      setIsEditingScript(false);
      setEditingScriptItems([]);
    }
  };

  const handleScriptItemChange = (
    index: number,
    field: string,
    value: string | boolean,
  ) => {
    const newItems = [...editingScriptItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setEditingScriptItems(newItems);
  };

  const handleScriptItemMove = (index: number, direction: "up" | "down") => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === editingScriptItems.length - 1) return;

    const newItems = [...editingScriptItems];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    [newItems[index], newItems[targetIndex]] = [
      newItems[targetIndex],
      newItems[index],
    ];
    setEditingScriptItems(newItems.map((item, i) => ({ ...item, order: i })));
  };

  const handleScriptItemDelete = (index: number) => {
    if (!window.confirm("この行を削除しますか？")) return;
    const newItems = editingScriptItems.filter((_, i) => i !== index);
    setEditingScriptItems(newItems.map((item, i) => ({ ...item, order: i })));
  };

  const handleScriptItemAdd = () => {
    setEditingScriptItems([
      ...editingScriptItems,
      {
        category: "",
        timeEstimate: "",
        itemName: "",
        question: "",
        note: "",
        isRequired: false,
        order: editingScriptItems.length,
      },
    ]);
  };

  const handleScriptItemsSave = async () => {
    if (!interviewId) return;

    setSaving("bulk");
    setError(null);
    try {
      const result = await bulkSaveScriptItems({
        interviewId,
        items: editingScriptItems.map((item, index) => ({
          scriptItemId: item.scriptItemId || undefined,
          category: item.category,
          timeEstimate: item.timeEstimate,
          itemName: item.itemName,
          question: item.question || undefined,
          note: item.note || undefined,
          isRequired: item.isRequired,
          order: index,
        })),
      });
      if (result?.success) {
        setIsEditingScript(false);
        setEditingScriptItems([]);
        refetchScript();
      } else if (result?.errorMessage) {
        setError(result.errorMessage);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "保存中にエラーが発生しました",
      );
    } finally {
      setSaving(null);
    }
  };

  const handleCompleteInterview = async () => {
    if (!interviewId) return;

    const errors: Record<string, string> = {};
    if (coachReportNg === null) {
      errors.coachReportNg = "担当コーチへのレポート共有を選択してください";
    }
    if (internalUseAllowed === null) {
      errors.internalUseAllowed = "社内公開の可否を選択してください";
    }
    if (hasConfidentialInfo && !confidentialInfo.trim()) {
      errors.confidentialInfo = "秘匿情報の内容を記入してください";
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});

    const confirmMessage = interview?.completionReport
      ? "完了報告を更新しますか？"
      : "インタビューを完了しますか？";
    if (!window.confirm(confirmMessage)) return;

    setCompleting(true);
    setError(null);
    try {
      // score 入力 UI は要約チェックページ側で扱うため、ここでは未入力 (0) で送る
      // backend 側で completion_report の score を別途上書き可能
      const result = await completeInterview({
        interviewId,
        handoverNote: handoverNote || undefined,
        coachReportNg: coachReportNg ?? false,
        interviewerInsight: interviewerInsight || undefined,
        internalUseAllowed: internalUseAllowed ?? false,
        hasConfidentialInfo,
        confidentialInfo: confidentialInfo || undefined,
        satisfactionScore: 0,
        behaviorChangeScore: 0,
      });
      if (result?.success) {
        refetchInterview();
      } else if (result?.errorMessage) {
        setError(result.errorMessage);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "完了処理中にエラーが発生しました",
      );
    } finally {
      setCompleting(false);
    }
  };

  const handleInlineEditStart = (
    itemId: string,
    field: "question" | "note" | "interviewerMemo",
    currentValue: string,
  ) => {
    setEditingCell({ itemId, field });
    setEditingValue(currentValue);
  };

  const handleInlineEditSave = () => {
    if (!editingCell) return;
    const { itemId, field } = editingCell;
    handleUpdateScriptItem(itemId, { [field]: editingValue });
    setEditingCell(null);
    setEditingValue("");
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${date.toLocaleDateString("ja-JP")} ${date.toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })}`;
  };

  if (initialLoading) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      </div>
    );
  }

  if (fetchError || error) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[#ef4444]">
          {fetchError || error}
        </div>
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[#ef4444]">
          インタビューが見つかりません
        </div>
      </div>
    );
  }

  const isLocked = COMPLETION_REPORT_LOCKED_STATUSES.includes(interview.status);
  const hasExistingReport = interview.completionReport !== null;
  const displayStatus = resolveInterviewDisplayStatus(interview);
  const statusColor = STATUS_COLORS[displayStatus] || {
    bg: "#f3f4f6",
    color: "#374151",
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="w-full">
        {/* Back Link */}
        <div className="mb-4">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-[13px] text-[var(--color-primary-500)]"
          >
            <ArrowLeft className="h-4 w-4" />
            戻る
          </button>
        </div>

        {/* Interview Header Card */}
        <div className="mb-6 overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
            <div>
              <h1 className="m-0 flex items-baseline gap-2 font-bold text-2xl text-[var(--color-primary-500)]">
                {interview.candidateName || "候補者"}
                {interview.candidateNameKana && (
                  <span className="font-normal text-[var(--color-text-secondary)] text-sm">
                    {interview.candidateNameKana}
                  </span>
                )}
              </h1>
              <p className="mt-1 text-[var(--color-text-secondary)] text-sm">
                日時: {formatDateTime(interview.startTime)}
              </p>
              <p className="mt-1 text-[var(--color-text-secondary)] text-sm">
                会社: {interview.sponsorName || "-"} / ターム:{" "}
                <TermNotionLink
                  termTitle={interview.termTitle}
                  notionUrl={interview.termNotionUrl}
                />
              </p>
              {interview.stakeholderContext && (
                <p className="mt-1 text-[var(--color-text-secondary)] text-sm">
                  {`${interview.stakeholderContext.recipientNames.join("、")}について聞きます（${interview.stakeholderContext.stakeholderSettingName}）`}
                </p>
              )}
            </div>
            <span
              className="inline-block rounded-full px-3 py-1 font-semibold text-xs"
              style={{
                backgroundColor: statusColor.bg,
                color: statusColor.color,
              }}
            >
              {STATUS_LABELS[displayStatus] || displayStatus}
            </span>
          </div>
          <div className="px-6 py-5">
            <div className="flex gap-3">
              {(interview.zoomMeetingStartUrl || interview.zoomMeetingUrl) && (
                <a
                  href={
                    interview.zoomMeetingStartUrl ||
                    interview.zoomMeetingUrl ||
                    ""
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex cursor-pointer items-center gap-1 rounded-md border-none bg-[var(--color-primary-500)] px-4 py-2 font-medium text-sm text-white no-underline"
                >
                  <Video className="h-4 w-4" />
                  Zoom会議に参加
                  <ExternalLink className="ml-0.5 h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Script Table Card */}
        <div className="mb-6 overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
            <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
              インタビュースクリプト
            </h2>
            <div className="flex gap-2">
              {isEditingScript ? (
                <>
                  <button
                    type="button"
                    onClick={handleCancelEditScript}
                    className="cursor-pointer rounded-md border border-[var(--color-text-secondary)] bg-white px-3 py-1.5 font-medium text-[var(--color-text-secondary)] text-sm"
                  >
                    キャンセル
                  </button>
                  <button
                    type="button"
                    onClick={handleScriptItemsSave}
                    disabled={saving === "bulk"}
                    className={`rounded-md border-none px-3 py-1.5 font-medium text-sm text-white ${saving === "bulk" ? "cursor-not-allowed bg-[#9ca3af]" : "cursor-pointer bg-[var(--color-primary-500)]"}`}
                  >
                    {saving === "bulk" ? "保存中..." : "保存"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleStartEditScript}
                  disabled={scriptBulkEditDisabled}
                  className={`rounded-md border border-[var(--color-primary-500)] bg-white px-3 py-1.5 font-medium text-[var(--color-primary-500)] text-sm transition-opacity ${scriptBulkEditDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                >
                  編集
                </button>
              )}
            </div>
          </div>
          <div className="p-6">
            {isEditingScript ? (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div className="text-[var(--color-text-secondary)] text-sm">
                    項目数: {editingScriptItems.length}件
                  </div>
                  <button
                    type="button"
                    onClick={handleScriptItemAdd}
                    className="inline-flex cursor-pointer items-center gap-1 rounded border border-[var(--color-primary-500)] bg-white px-2 py-1 font-medium text-[13px] text-[var(--color-primary-500)]"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    行を追加
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[var(--color-bg-subtle-gray)]">
                        <th className="w-[60px] p-2 text-center font-semibold text-[var(--color-text-secondary)] text-xs">
                          操作
                        </th>
                        <th className="p-2 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                          カテゴリ
                        </th>
                        <th className="whitespace-nowrap p-2 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                          経過時間
                        </th>
                        <th className="p-2 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                          項目名
                        </th>
                        <th className="w-10 p-2 text-center font-semibold text-[var(--color-text-secondary)] text-xs">
                          必須
                        </th>
                        <th className="p-2 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                          質問 / 発話内容
                        </th>
                        <th className="p-2 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                          補足
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingScriptItems.map((item, index) => (
                        <tr
                          key={`edit-${index}`}
                          className="border-[var(--color-border-default)] border-b"
                        >
                          <td className="p-2 text-center align-top">
                            <div className="flex flex-col items-center gap-0.5">
                              <button
                                type="button"
                                onClick={() =>
                                  handleScriptItemMove(index, "up")
                                }
                                disabled={index === 0}
                                className={`border-none bg-none p-0.5 ${index === 0 ? "cursor-default text-[#d1d5db]" : "cursor-pointer text-[var(--color-primary-500)]"}`}
                              >
                                <ArrowUp className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleScriptItemMove(index, "down")
                                }
                                disabled={
                                  index === editingScriptItems.length - 1
                                }
                                className={`border-none bg-none p-0.5 ${index === editingScriptItems.length - 1 ? "cursor-default text-[#d1d5db]" : "cursor-pointer text-[var(--color-primary-500)]"}`}
                              >
                                <ArrowDown className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleScriptItemDelete(index)}
                                className="cursor-pointer border-none bg-none p-0.5 text-[#ef4444]"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                          <td className="p-2 align-top">
                            <input
                              type="text"
                              value={item.category}
                              onChange={(e) =>
                                handleScriptItemChange(
                                  index,
                                  "category",
                                  e.target.value,
                                )
                              }
                              className="box-border w-full min-w-[100px] rounded border border-[var(--color-border-default)] px-2 py-1.5 text-[13px]"
                            />
                          </td>
                          <td className="p-2 align-top">
                            <input
                              type="text"
                              value={item.timeEstimate}
                              onChange={(e) =>
                                handleScriptItemChange(
                                  index,
                                  "timeEstimate",
                                  e.target.value,
                                )
                              }
                              className="box-border w-full min-w-[80px] rounded border border-[var(--color-border-default)] px-2 py-1.5 text-[13px]"
                            />
                          </td>
                          <td className="p-2 align-top">
                            <input
                              type="text"
                              value={item.itemName}
                              onChange={(e) =>
                                handleScriptItemChange(
                                  index,
                                  "itemName",
                                  e.target.value,
                                )
                              }
                              className="box-border w-full min-w-[120px] rounded border border-[var(--color-border-default)] px-2 py-1.5 text-[13px]"
                            />
                          </td>
                          <td className="p-2 text-center align-top">
                            <input
                              type="checkbox"
                              checked={item.isRequired}
                              onChange={(e) =>
                                handleScriptItemChange(
                                  index,
                                  "isRequired",
                                  e.target.checked,
                                )
                              }
                              className="accent-[var(--color-primary-500)]"
                            />
                          </td>
                          <td className="p-2 align-top">
                            <textarea
                              value={item.question}
                              onChange={(e) =>
                                handleScriptItemChange(
                                  index,
                                  "question",
                                  e.target.value,
                                )
                              }
                              rows={2}
                              className="box-border w-full min-w-[200px] resize-y rounded border border-[var(--color-border-default)] px-2 py-1.5 text-[13px]"
                            />
                          </td>
                          <td className="p-2 align-top">
                            <textarea
                              value={item.note}
                              onChange={(e) =>
                                handleScriptItemChange(
                                  index,
                                  "note",
                                  e.target.value,
                                )
                              }
                              rows={2}
                              className="box-border w-full min-w-[150px] resize-y rounded border border-[var(--color-border-default)] px-2 py-1.5 text-[13px]"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : scriptInitialLoading ? (
              <div className="py-8 text-center text-[var(--color-text-tertiary)]">
                読み込み中...
              </div>
            ) : sortedItems.length === 0 ? (
              <div className="py-8 text-center text-[#9ca3af]">
                スクリプトが設定されていません
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* インタビュー中に読むのは「質問 / 発話内容」なので、その列を一番広く取る。
                    列幅を固定しないと、本文の長さで列の配分が毎回変わってしまう */}
                <table className="w-full table-fixed border-collapse">
                  <thead>
                    <tr className="bg-[var(--color-bg-subtle-gray)]">
                      <th className="w-[10%] p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                        カテゴリ
                      </th>
                      {/* 幅を固定するので whitespace-nowrap は付けない。
                          「10〜15分」のような値が入ると列からはみ出す */}
                      <th className="w-[7%] p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                        経過時間
                      </th>
                      <th className="w-[15%] p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                        項目名
                      </th>
                      <th className="w-[34%] p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                        質問 / 発話内容
                      </th>
                      <th className="w-[17%] p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                        補足
                      </th>
                      <th className="w-[17%] p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                        メモ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((item) => (
                      <tr
                        key={item.scriptItemId}
                        className="border-[var(--color-border-default)] border-b"
                      >
                        <td className="p-3 font-medium text-sm">
                          {item.category}
                        </td>
                        <td className="p-3 text-[var(--color-text-secondary)] text-sm">
                          {item.timeEstimate || ""}
                        </td>
                        <td className="p-3 text-sm">
                          <span className="flex items-center gap-1">
                            {item.isRequired && (
                              <Star className="h-3.5 w-3.5 shrink-0 fill-[#fbbf24] text-[#fbbf24]" />
                            )}
                            {item.itemName}
                          </span>
                        </td>
                        <td
                          className="whitespace-pre-wrap break-words p-3 text-sm"
                          onDoubleClick={() =>
                            handleInlineEditStart(
                              item.scriptItemId,
                              "question",
                              item.question,
                            )
                          }
                        >
                          {editingCell?.itemId === item.scriptItemId &&
                          editingCell?.field === "question" ? (
                            <textarea
                              className="w-full resize-y rounded border border-[var(--color-border-default)] p-2 text-sm"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onBlur={handleInlineEditSave}
                              onKeyDown={(e) => {
                                if (e.key === "Escape") {
                                  setEditingCell(null);
                                  setEditingValue("");
                                }
                              }}
                              rows={3}
                            />
                          ) : (
                            item.question
                          )}
                          {saving === item.scriptItemId && (
                            <span className="ml-1 text-[11px] text-[var(--color-primary-500)]">
                              保存中...
                            </span>
                          )}
                        </td>
                        <td
                          className="whitespace-pre-wrap break-words p-3 text-[#888] text-sm"
                          onDoubleClick={() =>
                            handleInlineEditStart(
                              item.scriptItemId,
                              "note",
                              item.note || "",
                            )
                          }
                        >
                          {editingCell?.itemId === item.scriptItemId &&
                          editingCell?.field === "note" ? (
                            <textarea
                              className="w-full resize-y rounded border border-[var(--color-border-default)] p-2 text-sm"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onBlur={handleInlineEditSave}
                              onKeyDown={(e) => {
                                if (e.key === "Escape") {
                                  setEditingCell(null);
                                  setEditingValue("");
                                }
                              }}
                              rows={2}
                            />
                          ) : (
                            item.note || ""
                          )}
                        </td>
                        <td className="p-3 align-top">
                          {/* key に memo を含めると、保存で memo が変わるたびに入力欄が
                              作り直されて入力位置が失われる。この欄を書き換えるのは
                              本人だけなので、id だけで同一の入力欄として扱う */}
                          <MemoTextarea
                            key={item.scriptItemId}
                            defaultValue={item.memo || ""}
                            onBlur={(e) => {
                              const newValue = e.target.value;
                              if (newValue !== (item.memo || "")) {
                                handleUpdateScriptItem(item.scriptItemId, {
                                  interviewerMemo: newValue,
                                });
                              }
                            }}
                          />
                          {saving === item.scriptItemId && (
                            <span className="text-[11px] text-[var(--color-primary-500)]">
                              保存中...
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* 申し送り事項 Card */}
        <div className="mb-6 overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <div className="border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
            <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
              申し送り事項
            </h2>
          </div>
          <div className="p-6">
            {/* 1. mentoの担当者への申し送り */}
            <div className="mb-6">
              <label
                htmlFor="handoverNote"
                className="mb-2 block font-semibold text-[var(--color-text-primary)] text-sm"
              >
                mentoの担当者への申し送り
              </label>
              <textarea
                id="handoverNote"
                value={handoverNote}
                onChange={(e) => setHandoverNote(e.target.value)}
                placeholder="必要に応じてご記載ください（大きな不満や改善要求・担当コーチへの伝達事項など）"
                rows={3}
                className="box-border min-h-[80px] w-full resize-y rounded-lg border border-[var(--color-border-default)] p-3 text-sm"
              />
            </div>

            {/* 2. 担当コーチへのレポート共有 */}
            <div className="mb-6">
              <div className="mb-2 font-semibold text-[var(--color-text-primary)] text-sm">
                担当コーチへのレポート共有
              </div>
              <div className="flex gap-6 py-1">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="coachReportNg"
                    checked={coachReportNg === false}
                    onChange={() => {
                      setCoachReportNg(false);
                      setValidationErrors((prev) => {
                        const { coachReportNg: _, ...rest } = prev;
                        return rest;
                      });
                    }}
                    className="accent-[var(--color-primary-500)]"
                  />
                  <span className="text-sm">共有OK</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="coachReportNg"
                    checked={coachReportNg === true}
                    onChange={() => {
                      setCoachReportNg(true);
                      setValidationErrors((prev) => {
                        const { coachReportNg: _, ...rest } = prev;
                        return rest;
                      });
                    }}
                    className="accent-[var(--color-primary-500)]"
                  />
                  <span className="text-sm">共有NG</span>
                </label>
              </div>
              {validationErrors.coachReportNg && (
                <p className="mt-1 text-[#ef4444] text-[13px]">
                  {validationErrors.coachReportNg}
                </p>
              )}
            </div>

            {/* 3. インタビュアーとしての気づき */}
            <div className="mb-6">
              <label
                htmlFor="interviewerInsight"
                className="mb-2 block font-semibold text-[var(--color-text-primary)] text-sm"
              >
                インタビュアーとしての気づき
              </label>
              <textarea
                id="interviewerInsight"
                value={interviewerInsight}
                onChange={(e) => setInterviewerInsight(e.target.value)}
                placeholder="インタビューを通じて感じたことや気づきを記入してください"
                rows={3}
                className="box-border min-h-[80px] w-full resize-y rounded-lg border border-[var(--color-border-default)] p-3 text-sm"
              />
            </div>

            {/* 4. 社内公開の可否 */}
            <div className="mb-6">
              <div className="mb-1 font-semibold text-[var(--color-text-primary)] text-sm">
                社内公開の可否
              </div>
              <p className="mb-2 text-[var(--color-text-secondary)] text-xs">
                &quot;利用者の声&quot;としての社内活用可否
              </p>
              <div className="flex flex-col gap-3 py-1">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="internalUseAllowed"
                    checked={internalUseAllowed === true}
                    onChange={() => {
                      setInternalUseAllowed(true);
                      setValidationErrors((prev) => {
                        const { internalUseAllowed: _, ...rest } = prev;
                        return rest;
                      });
                    }}
                    className="accent-[var(--color-primary-500)]"
                  />
                  <span className="text-sm">公開可</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="internalUseAllowed"
                    checked={internalUseAllowed === false}
                    onChange={() => {
                      setInternalUseAllowed(false);
                      setValidationErrors((prev) => {
                        const { internalUseAllowed: _, ...rest } = prev;
                        return rest;
                      });
                    }}
                    className="accent-[var(--color-primary-500)]"
                  />
                  <span className="text-sm">公開してほしくない</span>
                </label>
              </div>
              {validationErrors.internalUseAllowed && (
                <p className="mt-1 text-[#ef4444] text-[13px]">
                  {validationErrors.internalUseAllowed}
                </p>
              )}
            </div>

            {/* 5. 秘匿情報の確認 */}
            <div className="mb-6">
              <div className="mb-2 font-semibold text-[var(--color-text-primary)] text-sm">
                秘匿情報の確認
              </div>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  checked={hasConfidentialInfo}
                  onChange={(e) => {
                    setHasConfidentialInfo(e.target.checked);
                    if (!e.target.checked) {
                      setValidationErrors((prev) => {
                        const { confidentialInfo: _, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  className="mt-[3px] accent-[var(--color-primary-500)]"
                />
                <span className="text-sm">
                  今回のインタビューにおいて秘匿情報（人事に伝えてほしくないこと・内緒にしておいてほしいこと）がある
                </span>
              </label>
            </div>

            {/* 6. 秘匿情報の内容 */}
            {hasConfidentialInfo && (
              <div className="mb-6">
                <label
                  htmlFor="confidentialInfo"
                  className="mb-2 block font-semibold text-[var(--color-text-primary)] text-sm"
                >
                  秘匿情報の内容
                </label>
                <textarea
                  id="confidentialInfo"
                  value={confidentialInfo}
                  onChange={(e) => {
                    setConfidentialInfo(e.target.value);
                    if (e.target.value.trim()) {
                      setValidationErrors((prev) => {
                        const { confidentialInfo: _, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="秘匿情報の内容を記入してください"
                  rows={3}
                  className="box-border min-h-[80px] w-full resize-y rounded-lg border border-[var(--color-border-default)] p-3 text-sm"
                />
                {validationErrors.confidentialInfo && (
                  <p className="mt-1 text-[#ef4444] text-[13px]">
                    {validationErrors.confidentialInfo}
                  </p>
                )}
              </div>
            )}

            {!isLocked && (
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={handleCompleteInterview}
                  disabled={completing}
                  className={`rounded-md border-none px-4 py-2 font-medium text-sm text-white ${completing ? "cursor-not-allowed bg-[#9ca3af]" : "cursor-pointer bg-[var(--color-primary-500)]"}`}
                >
                  {completing
                    ? "処理中..."
                    : hasExistingReport
                      ? "完了報告を更新"
                      : "インタビュー完了"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
